import { AccessDenied } from '../../routes/AuthRoutes';
import React, { useEffect, useRef, useState } from 'react';
import { Check, UsersRound, X } from 'lucide-react';
import { listRoles } from './userService';
import useFieldValidation from '../../components/useFieldValidation';
import { limitMobileDigits, validateUserDetails } from './CreateUserPage';
import RoleDropdown from './RoleDropdown';
import UserMobileField from './UserMobileField';
import { composeMobile, parseMobile } from '../../components/CustomerModal';

export default function UserEditModal({ user, onClose, onSubmit }) {
  const [form, setForm] = useState(() => {
    const parsed = parseMobile(user.mobile);
    return { name: user.name, email: user.email, mobile: parsed.national.replace(/\D/g, ''), dialIso: parsed.iso, role_id: String(user.role_id), is_active: user.is_active };
  });
  const { errors, setErrors, touchField } = useFieldValidation(form, validateUserDetails);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleLoadError, setRoleLoadError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setRoleLoadError(null);
    listRoles().then(rows => { if (active) { setRoles(rows); setErrors({}); } })
      .catch(error => {
        if (active) { setForbidden(error.status === 403); setRoleLoadError(error.message); }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt, setErrors]);
  useEffect(() => {
    function key(event) { if (event.key === 'Escape' && !busy.current) onClose(); }
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);
  async function submit(event) {
    event.preventDefault();
    if (busy.current || loading) return;
    const validation = validateUserDetails(form);
    if (!roles.some(role => String(role.id) === form.role_id)) validation.role_id = 'Select a valid role.';
    if (Object.keys(validation).length) { setErrors(validation); return; }
    busy.current = true; setSaving(true);
    try {
      await onSubmit(user.id, { ...form, name: form.name.trim(), email: form.email.trim().toLowerCase(), mobile: composeMobile(form.dialIso, form.mobile) });
      onClose();
    } catch (error) { setErrors({ form: error.message }); }
    finally { busy.current = false; setSaving(false); }
  }
  if (forbidden) return <AccessDenied />;
  return <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !busy.current) onClose(); }}>
    <form className="wh-form-modal" role="dialog" aria-modal="true" aria-labelledby="user-edit-title" onSubmit={submit} noValidate>
      <header className="wh-form-head"><span className="wh-form-head-icon"><UsersRound size={18} /></span><div><h2 id="user-edit-title">Edit User</h2><p>Update user details</p></div><button type="button" className="wh-form-close" aria-label="Close" disabled={saving} onClick={onClose}><X size={16} /></button></header>
      <div className="wh-form-scroll">
      <fieldset className="users-fields" disabled={saving}><div className="wh-form-body"><div className="wh-form-grid">
        {[['name', 'Name', 'text', 150], ['email', 'Email', 'email', 255]].map(([name, label, type, maxLength]) => <label className="wh-form-field" key={name} htmlFor={`edit-user-${name}`}>
          <span>{label}{name !== 'mobile' && <em> *</em>}</span>
          <span className={`wh-form-control${errors[name] ? ' invalid' : ''}`}><input autoFocus={name === 'name'} id={`edit-user-${name}`} type={type} placeholder={name === 'email' ? 'Enter email address' : 'Enter name'} maxLength={maxLength} value={form[name]} onChange={event => setForm(current => ({ ...current, [name]: name === 'mobile' ? limitMobileDigits(event.target.value) : event.target.value }))} onBlur={() => touchField(name)} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `edit-user-${name}-error` : undefined} /></span>
          {errors[name] && <small className="wh-form-error" id={`edit-user-${name}-error`}>{errors[name]}</small>}
        </label>)}
        <UserMobileField id="edit-user-mobile" value={form.mobile} iso={form.dialIso} error={errors.mobile} onChange={value => { setForm(current => ({ ...current, mobile: value })); setErrors(current => ({ ...current, form: undefined, mobile: undefined })); }} onCountryChange={iso => { setForm(current => ({ ...current, dialIso: iso })); setErrors(current => ({ ...current, form: undefined, mobile: undefined })); }} onBlur={() => touchField('mobile')} />
        <div className="wh-form-field"><span id="edit-user-role-label">Role <em>*</em></span>
          <RoleDropdown disabled id="edit-user-role" roles={roles} value={form.role_id} loading={loading} error={errors.role_id} onChange={value => setForm(current => ({ ...current, role_id: value }))} onBlur={() => touchField('role_id')} />
          {errors.role_id && <small id="edit-user-role-error" className="wh-form-error">{errors.role_id}</small>}
          {roleLoadError && <small className="wh-form-error" role="alert">{roleLoadError}</small>}
          {!loading && !roles.length && <button type="button" className="wh-form-cancel" onClick={() => setAttempt(value => value + 1)}>Retry loading roles</button>}
        </div>
        <label className="users-active"><input type="checkbox" checked={form.is_active} onChange={event => setForm(current => ({ ...current, is_active: event.target.checked }))} />Active</label>
      </div>{errors.form && <p className="wh-modal-error" role="alert">{errors.form}</p>}</div>
      </fieldset>
      </div>
      <footer className="wh-form-footer"><button type="button" className="wh-form-cancel" onClick={onClose} disabled={saving}>Cancel</button><button type="submit" className="wh-form-save" disabled={saving || loading || !roles.length}>{saving ? 'Saving...' : <><Check size={15} />Save Changes</>}</button></footer>
    </form>
  </div>;
}
